"use client"

import { useState } from "react"
import { Card, CardContent } from "@/components/ui/card"
import { Button } from "@/components/ui/button"
import { Input } from "@/components/ui/input"
import { MessageSquarePlus, Clock, User, Pencil, Trash2, ChevronDown, ChevronUp, Search } from "lucide-react"
import { RichTextEditor } from "../rich-text-editor"
import { getGetCommentsQueryKey, useDeleteCommentsCommentId, useGetComments, usePostComments, usePutCommentsCommentId } from "openapi/api/endpoints"
import { useQueryClient } from "@tanstack/react-query"
import { toast } from "sonner"
import { Comment } from "openapi/api/model"
import { RichText } from "@/components/graphite/rich-text"


export function PersonNotes({ personId }: { personId: string }) {
    const { mutateAsync: updateComment, isPending: isSubmitting } = usePutCommentsCommentId()
    const { mutateAsync: postComment } = usePostComments()
    const queryClient = useQueryClient()
    const {data:notes}=useGetComments({entity_type:'contacts',entity_id:personId})
    const {mutateAsync:deleteComment}=useDeleteCommentsCommentId()
    const [showCreateForm, setShowCreateForm] = useState(false)
    const [newNoteContent, setNewNoteContent] = useState("")
    const [editingNoteId, setEditingNoteId] = useState<string | null>(null)
    const [editNoteContent, setEditNoteContent] = useState("")
    const [expandedNotes, setExpandedNotes] = useState<Set<string>>(new Set())
    const [searchQuery, setSearchQuery] = useState("")

    const handleCreateNote = async () => {
        try{
            if (!newNoteContent.trim()) return

            await postComment({
                data: {
                    label: 'note',
                    body: newNoteContent,
                    entity_type: 'contacts',
                    entity_id: personId,
                }
            })
            toast.success("Created!",{
                description: 'Comment created successfully',
                action: {
                label: "Dismiss",
                onClick: () => {},
                },
                style: {
                    background: '#a3e635'
                }
            })
            await queryClient.invalidateQueries({ queryKey:getGetCommentsQueryKey({entity_type:'contacts',entity_id:personId})  })
            setNewNoteContent("")
            setShowCreateForm(false)            
        }catch(err){
            toast.error("Failed to add comment!",{
                description: `${err}`,
                className: "text-white",
                descriptionClassName: "text-white",
                action: {
                label: "Dismiss",
                onClick: () => {},
                },
                style: {
                background: "#dc2626",
                },
            });
        }

    }

    const handleEditNote = (note: Comment) => {
        setEditingNoteId(note.id??'')
        setEditNoteContent(note.body??'')
        setShowCreateForm(false)
    }

    const handleSaveEdit = async () => {
        if (!editNoteContent.trim() || !editingNoteId) return
        try{
            await updateComment({commentId: editingNoteId as string, 
                data:{
                    label: 'note',
                    body:editNoteContent
                }
            })
            toast.success("Updated!",{
                description: 'Comment updated successfully',
                action: {
                label: "Dismiss",
                onClick: () => {},
                },
                style: {
                    background: '#a3e635'
                }
            })
            await queryClient.invalidateQueries({ queryKey:getGetCommentsQueryKey({entity_type:'contacts',entity_id:personId})  })
            setEditingNoteId(null)
            setEditNoteContent("")
        }catch(err){
            toast.error("Failed to update comment!",{   
                description: `${err}`,
                className: "text-white",
            })
    }}

    const handleCancelEdit = () => {
        setEditingNoteId(null)
        setEditNoteContent("")
    }

    const handleDeleteNote = async (noteId: string) => {
        if (!confirm("Are you sure you want to delete this note?")) return
        try{
            await deleteComment({commentId: noteId as string})
            toast.success("Deleted!",{
                description: 'Comment deleted successfully',
                action: {
                label: "Dismiss",
                onClick: () => {},
                },
                style: {
                    background: '#a3e635'
                }
            })
            await queryClient.invalidateQueries({ queryKey:getGetCommentsQueryKey({entity_type:'contacts',entity_id:personId})  })
        }catch(err){
            toast.error("Failed to delete comment!",{
                description: `${err}`,
                className: "text-white",
                descriptionClassName: "text-white",
                action: {
                label: "Dismiss",
                onClick: () => {},
                },
                style: {
                background: "#dc2626",
                },
            }); 
        }

    }

    const isLongNote = (content: string) => {
        const textContent = content.replace(/<[^>]*>/g, "")
        return textContent.length > 300
    }

    const getPreviewContent = (content: string) => {
        const textContent = content.replace(/<[^>]*>/g, "")
        if (textContent.length <= 300) return content

        const preview = textContent.substring(0, 300)
        const lastPeriod = preview.lastIndexOf(".")
        const lastSpace = preview.lastIndexOf(" ")
        const breakPoint = lastPeriod > 200 ? lastPeriod + 1 : lastSpace > 0 ? lastSpace : 300

        return textContent.substring(0, breakPoint) + "..."
    }

    const toggleNoteExpansion = (noteId: string) => {
        setExpandedNotes((prev) => {
            const newSet = new Set(prev)
            if (newSet.has(noteId)) {
                newSet.delete(noteId)
            } else {
                newSet.add(noteId)
            }
            return newSet
        })
    }

    const filteredNotes = notes?.filter((note) => {
        const query = searchQuery.toLowerCase()
        const textContent = note?.body?.replace(/<[^>]*>/g, "").toLowerCase()
        return textContent?.includes(query) || note.username?.toLowerCase().includes(query)
    })

    return (
        <div className="space-y-4">
            <div className="flex items-center justify-between gap-4">
                <div className="relative flex-1">
                    <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                    <Input
                        placeholder="Search notes by content, author, or tags..."
                        value={searchQuery}
                        onChange={(e) => setSearchQuery(e.target.value)}
                        className="pl-9"
                    />
                </div>
                {!showCreateForm && !editingNoteId && (
                    <Button onClick={() => setShowCreateForm(true)} size="sm" className="shrink-0">
                        <MessageSquarePlus className="h-4 w-4 mr-2" />
                        New Note
                    </Button>
                )}
            </div>

            {showCreateForm && (
                <Card className="border-primary/50">
                    <CardContent className="pt-6 space-y-3">
                        <RichTextEditor
                            content={newNoteContent}
                            onChange={setNewNoteContent}
                            placeholder="Write your note here..."
                        />
                        <div className="flex gap-2">
                            <Button onClick={handleCreateNote} disabled={isSubmitting || !newNoteContent.trim()}>
                                {isSubmitting ? "Saving..." : "Save Note"}
                            </Button>
                            <Button
                                variant="outline"
                                onClick={() => {
                                    setShowCreateForm(false)
                                    setNewNoteContent("")
                                }}
                                disabled={isSubmitting}
                            >
                                Cancel
                            </Button>
                        </div>
                    </CardContent>
                </Card>
            )}

            {filteredNotes?.length === 0 ? (
                <div className="text-center py-8 text-muted-foreground">
                    <MessageSquarePlus className="h-12 w-12 mx-auto mb-2 opacity-50" />
                    <p>
                        {searchQuery ? "No notes match your search." : "No notes yet. Click 'New Note' to add your first note."}
                    </p>
                </div>
            ) : (
                <div className="space-y-3">
                    {filteredNotes?.map((note) => (
                        <div key={note.id}>
                            {editingNoteId === note.id ? (
                                <Card className="border-primary/50">
                                    <CardContent className="pt-6 space-y-3">
                                        <RichTextEditor
                                            content={editNoteContent}
                                            onChange={setEditNoteContent}
                                            placeholder="Write your note here..."
                                        />
                                        <div className="flex gap-2">
                                            <Button onClick={handleSaveEdit} disabled={isSubmitting || !editNoteContent.trim()}>
                                                {isSubmitting ? "Saving..." : "Save Changes"}
                                            </Button>
                                            <Button variant="outline" onClick={handleCancelEdit} disabled={isSubmitting}>
                                                Cancel
                                            </Button>
                                        </div>
                                    </CardContent>
                                </Card>
                            ) : (
                                <Card className="hover:shadow-md transition-shadow bg-muted">
                                    <CardContent className="pt-4">
                                        <div className="flex items-start justify-between mb-2">
                                            <div className="flex items-center gap-2 text-sm text-muted-foreground">
                                                <User className="h-4 w-4" />
                                                <span className="font-medium">{note.username}</span>
                                            </div>
                                            <div className="flex items-center gap-3">
                                                <div className="flex items-center gap-1 text-xs text-muted-foreground">
                                                    <Clock className="h-3 w-3" />
                                                    {new Date(note.created_at??'').toLocaleDateString("en-US", {
                                                        month: "short",
                                                        day: "numeric",
                                                        year: "numeric",
                                                        hour: "numeric",
                                                        minute: "2-digit",
                                                    })}
                                                </div>
                                                <div className="flex gap-1">
                                                    <Button
                                                        variant="ghost"
                                                        size="sm"
                                                        className="h-8 w-8 p-0"
                                                        onClick={() => handleEditNote(note)}
                                                        disabled={isSubmitting}
                                                    >
                                                        <Pencil className="h-3.5 w-3.5" />
                                                    </Button>
                                                    <Button
                                                        variant="ghost"
                                                        size="sm"
                                                        className="h-8 w-8 p-0 text-destructive hover:text-destructive"
                                                        onClick={() => handleDeleteNote(note.id??'')}
                                                        disabled={isSubmitting}
                                                    >
                                                        <Trash2 className="h-3.5 w-3.5" />
                                                    </Button>
                                                </div>
                                            </div>
                                        </div>
                                        <RichText
                                            className="mb-2"
                                            html={
                                                isLongNote(note.body ?? "") && !expandedNotes.has(note.id ?? "")
                                                    ? getPreviewContent(note.body ?? "")
                                                    : note.body ?? ""
                                            }
                                        />
                                        {isLongNote(note.body??'') && (
                                            <Button
                                                variant="ghost"
                                                size="sm"
                                                className="h-auto p-0 text-xs text-brand-ink hover:text-brand-ink hover:bg-transparent"
                                                onClick={() => toggleNoteExpansion(note.id??'')}
                                            >
                                                {expandedNotes.has(note.id??'') ? (
                                                    <>
                                                        <ChevronUp className="h-3 w-3 mr-1" />
                                                        Show less
                                                    </>
                                                ) : (
                                                    <>
                                                        <ChevronDown className="h-3 w-3 mr-1" />
                                                        Read more
                                                    </>
                                                )}
                                            </Button>
                                        )}
                                    </CardContent>
                                </Card>
                            )}
                        </div>
                    ))}
                </div>
            )}
        </div>
    )
}
